/**
 * BudgetPage (Phase 3, §6.2) — the per-month budget view.
 *
 * A month switcher over a `YYYY-MM` state (default: the current Berlin month)
 * consumes `GET /api/reports/month` for spent + budgeted + progress + totals,
 * joining bucket metadata from the single `['buckets']` cache. Each non-excluded
 * bucket gets an inline de-DE amount input (optimistic PUT /api/budgets);
 * "Vormonat kopieren" copies the previous month's budgets (with an overwrite
 * confirm when the current month already has any). Excluded / Transfer buckets
 * are never rendered but still net out of the totals server-side.
 */
import { useMemo, useState } from 'react';
import { Copy, FolderOpen } from 'lucide-react';
import { Link } from 'react-router';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { AmountText } from '../components/ui/AmountText.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { StaleDataNotice } from '../components/ui/StaleDataNotice.tsx';
import { money } from '../lib/format.ts';
import { useBucketTree } from '../lib/hooks/useBuckets.ts';
import { useCopyBudgets, useUpsertBudget } from '../lib/hooks/useBudgets.ts';
import { currentMonth, prevMonth, useReportMonth } from '../lib/hooks/useReports.ts';
import { useOnlineStatus } from '../context/OnlineStatusContext.tsx';
import { useToast } from '../context/ToastContext.tsx';
import type { MonthBucketRow } from '../lib/types.ts';
import { MonthSwitcher } from './budget/MonthSwitcher.tsx';
import { BudgetGroupSection } from './budget/BudgetGroupSection.tsx';
import { FixkostenCard } from './budget/FixkostenCard.tsx';
import { visibleBudgetGroups } from './budget/budgetMath.ts';

export default function BudgetPage() {
  const [month, setMonth] = useState(currentMonth());
  const { data, isLoading, isError, error, refetch } = useReportMonth(month);
  // Archived included: the server keeps archived buckets with month data in
  // the view + totals so historical months still render; `visibleBudgetGroups`
  // filters against the month data instead of statically dropping them.
  const tree = useBucketTree(true);
  const { online, guardOnline } = useOnlineStatus();
  const { toast } = useToast();

  const upsert = useUpsertBudget(month);
  const copy = useCopyBudgets();
  const [confirmCopy, setConfirmCopy] = useState(false);

  const rowsByBucket = useMemo(() => {
    const map = new Map<number, MonthBucketRow>();
    for (const row of data?.buckets ?? []) map.set(row.bucket_id, row);
    return map;
  }, [data]);

  const groups = useMemo(() => visibleBudgetGroups(tree, rowsByBucket), [tree, rowsByBucket]);

  const handleSetBudget = (bucketId: number, amount: string | null) => {
    if (!guardOnline()) return;
    upsert.mutate({ bucket_id: bucketId, amount });
  };

  const hasBudgets = (data?.buckets ?? []).some((r) => r.budgeted !== null);

  const runCopy = (overwrite: boolean) => {
    setConfirmCopy(false);
    if (!guardOnline()) return;
    copy.mutate(
      { from_month: prevMonth(month), to_month: month, overwrite },
      {
        onSuccess: (result) => {
          const parts = [`${result.copied} übernommen`];
          if (result.skipped > 0) parts.push(`${result.skipped} übersprungen`);
          toast(`Budgets aus dem Vormonat: ${parts.join(', ')}.`, {
            variant: result.copied > 0 ? 'success' : 'info',
          });
        },
        onError: (err) => {
          toast(err instanceof Error ? err.message : 'Budgets konnten nicht kopiert werden.', {
            variant: 'error',
          });
        },
      },
    );
  };

  const onCopyClick = () => {
    if (hasBudgets) setConfirmCopy(true);
    else runCopy(false);
  };

  const totals = data?.totals;

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Budget"
        subtitle="Ausgaben gegen Budget je Kategorie"
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={onCopyClick}
            disabled={!online || copy.isPending}
          >
            <Copy className="h-4 w-4" aria-hidden />
            Vormonat kopieren
          </Button>
        }
      />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        <div className="flex items-center justify-center">
          <MonthSwitcher month={month} onChange={setMonth} />
        </div>

        <FixkostenCard month={month} />

        {isLoading ? (
          <SkeletonList rows={6} />
        ) : isError && data === undefined ? (
          // Full-page error only without cached data — a failed background
          // refetch keeps the last loaded month visible (§5.6).
          <ErrorState title="Budget konnte nicht geladen werden" error={error} onRetry={refetch} />
        ) : groups.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title="Noch keine Kategorien"
            description="Lege zuerst Kategorien an, um Budgets zu vergeben."
            action={
              <Link to="/kategorien">
                <Button variant="primary" size="sm">
                  Zu den Kategorien
                </Button>
              </Link>
            }
          />
        ) : (
          <>
            {isError ? <StaleDataNotice onRetry={refetch} /> : null}

            {groups.map((group) => (
              <BudgetGroupSection
                key={group.id}
                group={group}
                rowsByBucket={rowsByBucket}
                disabled={!online}
                onSetBudget={handleSetBudget}
              />
            ))}

            {totals ? (
              <section>
                <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                  Gesamt
                </h2>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  <TotalCell label="Budgetiert" value={money(totals.budgeted_total)} />
                  <TotalCell label="Ausgegeben" value={money(totals.spent_total)} />
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-xs text-slate-400 dark:text-slate-500">Verbleibend</dt>
                    <dd className="text-sm tabular-nums">
                      <AmountText value={totals.remaining_total} />
                    </dd>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <dt className="text-xs text-slate-400 dark:text-slate-500">
                      Netto (Einnahmen − Ausgaben)
                    </dt>
                    <dd className="text-sm tabular-nums">
                      <AmountText value={totals.net} />
                    </dd>
                  </div>
                </dl>
              </section>
            ) : null}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmCopy}
        onOpenChange={setConfirmCopy}
        title="Budgets überschreiben?"
        message="Dieser Monat hat bereits Budgets. Sollen die Budgets des Vormonats sie überschreiben?"
        confirmLabel="Überschreiben"
        loading={copy.isPending}
        onConfirm={() => runCopy(true)}
      />
    </div>
  );
}

function TotalCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-slate-400 dark:text-slate-500">{label}</dt>
      <dd className="text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
        {value}
      </dd>
    </div>
  );
}
